<template>
  <div>
    <div class="multiselect-wrap--small">
      <contact-details-item
        compact
        :title="$t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL')"
      >
        <template v-slot:button>
          <woot-button
            v-if="showSelfAssign"
            icon="arrow-right"
            class="btn-primary"
            variant="link"
            size="small"
            :disabled="isBlockChannel"
            @click="onSelfAssign"
          >
            {{ $t('CONVERSATION_SIDEBAR.SELF_ASSIGN') }}
          </woot-button>
        </template>
      </contact-details-item>
      <multiselect-dropdown
        :options="agentTransferList"
        :selected-item="assignedAgent"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
        "
        :disabled="isBlockChannel"
        @click="onClickAssignAgent"
      />
    </div>
    <div class="multiselect-wrap--small">
      <contact-details-item
        compact
        :title="$t('CONVERSATION_SIDEBAR.TEAM_LABEL')"
      />
      <multiselect-dropdown
        :options="teamsList"
        :selected-item="assignedTeam"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.TEAM')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.TEAM')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.INPUT')
        "
        :disabled="isBlockChannel"
        @click="onClickAssignTeam"
      />
    </div>

    <div class="protocol">
      <contact-details-item
        :title="$t('CONVERSATION_SIDEBAR.PROTOCOL')"
        class="protocol-contact-details-item"
        compact
      />
      <span v-if="currentChat.protocol_id" class="protocol_Id--display-text">
        {{ currentChat.protocol_id }}
      </span>
    </div>

    <div class="multiselect-wrap--small">
      <contact-details-item
        compact
        :title="$t('CONVERSATION.PRIORITY.TITLE')"
      />
      <multiselect-dropdown
        :options="priorityOptions"
        :selected-item="assignedPriority"
        :multiselector-title="$t('CONVERSATION.PRIORITY.TITLE')"
        :multiselector-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SELECT_PLACEHOLDER')
        "
        :no-search-result="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.NO_RESULTS')
        "
        :input-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.INPUT_PLACEHOLDER')
        "
        :disabled="isBlockChannel"
        @click="onClickAssignPriority"
      />
    </div>
    <contact-details-item
      compact
      :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_LABELS')"
    />
    <conversation-labels
      :conversation-id="conversationId"
      :disabled="isBlockChannel"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import adminMixin from 'dashboard/mixins/isAdmin';
import ContactDetailsItem from './ContactDetailsItem.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import ConversationLabels from './labels/LabelBox.vue';
import agentMixin from 'dashboard/mixins/agentMixin';
import teamMixin from 'dashboard/mixins/conversation/teamMixin';
import { CONVERSATION_PRIORITY } from '../../../../shared/constants/messages';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import router from '../..';
import { closeConversation } from '../../../helper/URLHelper';

export default {
  components: {
    ContactDetailsItem,
    MultiselectDropdown,
    ConversationLabels,
  },
  mixins: [agentMixin, alertMixin, teamMixin, adminMixin],
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
    inboxId: {
      type: Number,
      default: undefined,
    },
    channelType: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      priorityOptions: [
        {
          id: null,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
          thumbnail: `/assets/images/dashboard/priority/none.svg`,
          key: 'priority'
        },
        {
          id: CONVERSATION_PRIORITY.URGENT,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.URGENT}.svg`,
          key: 'priority'
        },
        {
          id: CONVERSATION_PRIORITY.HIGH,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.HIGH}.svg`,
          key: 'priority'
        },
        {
          id: CONVERSATION_PRIORITY.MEDIUM,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.MEDIUM}.svg`,
          key: 'priority'
        },
        {
          id: CONVERSATION_PRIORITY.LOW,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.LOW}.svg`,
          key: 'priority'
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      activeInbox: 'getSelectedInbox',
      accountId: 'getCurrentAccountId',
    }),
    isBlockChannel() {
      const isGroup = this.currentChat?.meta?.sender?.phone_number?.includes(
        '@g.us'
      );
      const isChannelInternal = this.channelType === 'Channel::Internal';
      return isGroup || isChannelInternal;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.activeInbox);
    },

    agentTransferList() {
      const agentFilters = this.agentsList.filter(
        agent => agent.id !== this.currentUser.id
      );
      const assign_to_me = this.currentUser.permissions.assign_to_me;
      return assign_to_me ? this.agentsList : agentFilters;
    },

    assignedAgent: {
      get() {
        if (
          !this.currentUser?.permissions?.see_conversations_team &&
          !this.currentUser?.permissions?.see_all_conversations &&
          this.currentChat?.meta?.assignee &&
          this.currentChat?.meta?.assignee?.id !== this.currentUser?.id &&
          this.currentChat?.meta?.sender?.agent_id !== this.currentUser?.id
        ) {
          closeConversation(
            this.accountId,
            router,
            this.currentUser?.id,
            this.currentChat?.meta?.assignee?.id,
            this.activeInbox
          );
        }
        return this.currentChat.meta.assignee;
      },
      set(agent) {
        const agentId = agent ? agent.id : 0;
        this.$store.dispatch('setCurrentChatAssignee', agent);
        this.$store
          .dispatch('assignAgent', {
            conversationId: this.currentChat.id,
            agentId,
          })
          .then(() => {
            this.showAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
            closeConversation(
              this.accountId,
              router,
              this.currentUser?.id,
              agentId,
              this.activeInbox
            );
          });
      },
    },
    assignedTeam: {
      get() {
        return this.currentChat.meta.team;
      },
      set(team) {
        const conversationId = this.currentChat.id;
        const teamId = team ? team.id : 0;
        this.$store.dispatch('setCurrentChatTeam', { team, conversationId });
        this.$store
          .dispatch('assignTeam', { conversationId, teamId })
          .then(() => {
            this.showAlert(this.$t('CONVERSATION.CHANGE_TEAM'));
          });
      },
    },
    assignedPriority: {
      get() {
        const selectedOption = this.priorityOptions.find(
          opt => opt.id === this.currentChat.priority
        );

        return selectedOption || this.priorityOptions[0];
      },
      set(priorityItem) {
        const conversationId = this.currentChat.id;
        const oldValue = this.currentChat?.priority;
        const priority = priorityItem ? priorityItem.id : null;

        this.$store.dispatch('setCurrentChatPriority', {
          priority,
          conversationId,
        });
        this.$store
          .dispatch('assignPriority', { conversationId, priority })
          .then(() => {
            this.$track(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
              oldValue,
              newValue: priority,
              from: 'Conversation Sidebar',
            });
            this.showAlert(
              this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
                priority: priorityItem.name,
                conversationId,
              })
            );
          });
      },
    },
    showSelfAssign() {
      if (!this.assignedAgent) {
        return true;
      }

      if (
        this.currentUser.permissions.assign_to_me &&
        this.assignedAgent.id !== this.currentUser.id
      ) {
        return true;
      }

      if (this.inbox.enable_fowarding_conversations) {
        return true;
      }
      return false;
    },
    enableAssignAgent() {
      if (
        !this.inbox.enable_fowarding_conversations &&
        !this.currentUser.permissions.assign_to_me
      ) {
        return true;
      }
      return false;
    },
  },
  methods: {
    onSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.assignedAgent = selfAssign;
    },
    onClickAssignAgent(selectedItem) {
      if (this.assignedAgent && this.assignedAgent.id === selectedItem.id) {
        this.assignedAgent = null;
      } else {
        this.assignedAgent = selectedItem;
      }
    },

    onClickAssignTeam(selectedItemTeam) {
      if (this.assignedTeam && this.assignedTeam.id === selectedItemTeam.id) {
        this.assignedTeam = null;
      } else {
        this.assignedTeam = selectedItemTeam;
      }
    },

    onClickAssignPriority(selectedPriorityItem) {
      const isSamePriority =
        this.assignedPriority &&
        this.assignedPriority.id === selectedPriorityItem.id;

      this.assignedPriority = isSamePriority ? null : selectedPriorityItem;
    },
  },
};
</script>

<style lang="scss" scoped>
.protocol {
  margin-bottom: var(--space-small);

  .protocol_Id--display-text {
    background-color: #01c6d8;
    padding: 3px 5px 3px;
    font-size: var(--font-size-mini);
    color: #ffffff;
    border-radius: var(--border-radius-small);
  }

  .protocol-title {
    font-weight: 500;
    color: #1f2d3d;
    font-style: normal;
    font-size: 1.4rem;
  }

  .protocol_Id--display-text {
    border-radius: 100px;
    padding: 3px 10px;
    font-weight: var(--font-weight-bold);
  }
}

// Rótulos de campo (Agente / Equipe / Prioridade) no padrão do protótipo
::v-deep .conv-details--item__label {
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-medium);
  color: var(--s-500);
  text-transform: none;
  letter-spacing: normal;
  margin-bottom: var(--space-smaller);
  padding: 0;
}

.multiselect-wrap--small {
  margin-bottom: var(--space-normal);
}

// Selects (dropdowns) como caixas com borda arredondada — igual ao .select do protótipo
::v-deep .selector-button {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-small);
  padding: var(--space-small) var(--space-normal);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-normal);
  background: var(--white);
  transition: border-color 0.15s ease-in-out;

  &:hover {
    border-color: var(--s-300);
  }
}
</style>
